/**
 * `<SelectField>` — pick ONE option from a page-drawn dropdown, for
 * `ActionModal`'s `select` field kind (desktop-one-ground, 2026-10-24).
 *
 * Why not the native `<select>` it replaced: its popup is drawn by the OS, not
 * the page (see `PopMenu`). On a Mac, Chrome opens it as a system menu laid
 * over the box so that the CURRENT option sits on the box's text — for the
 * Add-member form's Gender, whose value is "—", that is a narrow list hanging
 * from the left of a full-width box whose arrow sits at the far right (Mario:
 * "it's on the very right, and then when you click on it, it's not quite on
 * it"). This panel hangs directly under the box, at the box's width, whatever
 * is chosen.
 *
 * Composed from `MultiSelectField` rather than invented: its trigger and panel
 * classes (and so their dark skin) and its outside-click + Escape close. The
 * arrow keys move the highlight (`moveSearchableActiveIndex`, as in
 * `SearchableSelect`), Enter or Space picks, Escape closes back onto the
 * trigger and Tab closes in passing.
 *
 * The host must NOT wrap it in a `<label>`: a click on an option inside a label
 * is re-dispatched to the label's control — the trigger — which would open the
 * list again as it closes. Name it with `labelledBy` instead.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { moveSearchableActiveIndex } from "../lib/searchable-select";

export interface SelectFieldOption {
  readonly value: string;
  readonly label: string;
}

export function SelectField({
  options,
  value,
  onChange,
  labelledBy,
}: {
  readonly options: ReadonlyArray<SelectFieldOption>;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Id of the element naming the field (the modal field's caption). */
  readonly labelledBy: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const chosen = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // The open list takes focus so the arrow keys reach it; the highlighted row
  // is announced through `aria-activedescendant`.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  const openList = (): void => {
    setActive(options.findIndex((o) => o.value === value));
    setOpen(true);
  };

  const close = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const pick = (v: string): void => {
    onChange(v);
    close();
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openList();
    }
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) =>
        moveSearchableActiveIndex(i, e.key === "ArrowDown" ? 1 : -1, options.length),
      );
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      setActive(options.length === 0 ? -1 : e.key === "Home" ? 0 : options.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const o = options[active];
      if (o !== undefined) pick(o.value);
    } else if (e.key === "Escape") {
      // Kept to this list: a page-level Escape handler (the nav's More panel,
      // a drawer behind the modal) must not act on it as well.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className="multi-select select-field" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="multi-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        {...(open ? { "aria-controls": listId } : {})}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onTriggerKey}
      >
        <span className="multi-select-trigger-label">{chosen?.label ?? ""}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div
          id={listId}
          ref={listRef}
          className="multi-select-menu"
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelledBy}
          {...(active >= 0 ? { "aria-activedescendant": `${listId}-${active}` } : {})}
          onKeyDown={onListKey}
        >
          {options.map((o, i) => (
            <div
              key={o.value}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.value === value}
              className={`multi-select-option select-field-option${
                i === active ? " active" : ""
              }`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o.value)}
            >
              {o.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
